import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { X, ChevronRight } from 'lucide-react';
import { groupByVarietyType, mergeVarietyRows, getShipmentWeightNested, convertWeight, weightUnitLabel } from '../../../utils/groupByVarietyType';
import { predictedEta } from '../../../utils/shipmentSchedule';
import { regionMarkerId, STATUS_COLORS, STATUS_LABELS } from './weatherConstants';
import { formatHarvestDelta, formatTonsCompact } from './harvestAlertUtils';
import { classifyAirTruckRow } from './sidebarAirTruck';

const CARD_CLASS =
  'shrink-0 overflow-hidden rounded-[12px] border border-[#E8EBE9] bg-white dark:border-[#30363d] dark:bg-[#1d1d1d]';

const CARD_DIVIDER = 'border-[#E8EBE9] dark:border-[#30363d]';

/** Small uppercase caption used for group names and the TOTAL footer. */
const CAPTION_CLASS =
  "font-['Inter'] text-[10px] font-medium uppercase leading-[15px] tracking-[0.06em] text-[#828382]";

/**
 * One panel card. Every card carries its own title + close button — with
 * arrivals split per commodity, the column can stack several of them.
 */
const PanelCard = ({ title, subtitle, onClose, children }) => (
  <div className={CARD_CLASS}>
    <div className="flex items-start justify-between gap-[8px] px-[14px] pb-[10px] pt-[12px]">
      <div className="min-w-0">
        <h3 className="font-['Inter'] text-[14px] font-semibold leading-[21px] text-[#282A29] dark:text-[#f0f5f2]">
          {title}
        </h3>
        {subtitle ? (
          <p className="font-['Inter'] mt-[2px] text-[11px] leading-[16px] text-[#828382]">
            {subtitle}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="shrink-0 cursor-pointer rounded p-[2px] text-[#828382] transition-colors hover:bg-[#F0F4F1] hover:text-[#282A29] dark:hover:bg-[#282a29] dark:hover:text-[#f0f5f2]"
      >
        <X size={16} />
      </button>
    </div>
    {children}
  </div>
);

const MapSidebar = ({ mode, timeWindow = 7, onClose, shipments = [], weatherAlerts = [], extremeAlerts = [], harvestAlerts = [], onHoverRegion, onFocusRegion, weightUnit = 'boxes', selectedCommodity = '' }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!mode) return null;

  const arrivalsTitle = timeWindow === 3
    ? t('map.sidebar.arrivalsTitle3', 'Arriving in 3 Days')
    : timeWindow === 10
      ? t('map.sidebar.arrivalsTitle10', 'Arriving in 10 Days')
      : t('map.sidebar.arrivalsTitle', 'Arriving in 7 Days');

  // Column of cards sitting beside the map card, on the page background.
  //
  // Mobile/portrait (< lg): non-modal bottom sheet so the map stays visible
  // behind it (ported from portrait-signage 18a161b/d1e391d). Desktop (lg+):
  // this outer wrapper becomes the static in-flow flex column beside the map
  // card (lg:static lg:flex lg:flex-col); the inner wrapper below dissolves
  // via `lg:contents` so its children become direct items of this column —
  // pixel-identical to the original layout.
  // z-[2100] sits below the mobile nav drawer (scrim/panel now 2200/2210) and
  // above the BiQ launcher (z-[2000]), so the drawer always wins over the sheet.
  return (
    <div className="scrollbar-hide fixed inset-x-0 bottom-0 z-[2100] max-h-[70dvh] w-full overflow-y-auto overscroll-contain rounded-t-2xl border-t border-[#E8EBE9] bg-[#F9F9F9] shadow-2xl dark:border-[#30363d] dark:bg-[#191919] lg:static lg:inset-auto lg:z-auto lg:flex lg:h-full lg:w-[320px] lg:max-h-none lg:shrink-0 lg:flex-col lg:gap-[12px] lg:overflow-y-auto lg:rounded-none lg:border-t-0 lg:bg-transparent dark:lg:bg-transparent lg:shadow-none">
      {/* Drag grabber — mobile bottom-sheet affordance only */}
      <div className="flex justify-center pt-2 pb-1 lg:hidden">
        <span className="h-1 w-10 rounded-full bg-[#C3C4C3] dark:bg-[#828382]" />
      </div>
      {/* Desktop (lg+): `lg:contents` dissolves this wrapper so its children
          become direct flex items of the outer column above. */}
      <div className="flex flex-col gap-[12px] px-[12px] pb-[16px] lg:contents">
        {mode === 'arrivals' && (
          <ArrivalsList
            shipments={shipments}
            t={t}
            timeWindow={timeWindow}
            weightUnit={weightUnit}
            title={arrivalsTitle}
            onClose={onClose}
            selectedCommodity={selectedCommodity}
          />
        )}
        {mode === 'alerts' && (
          <PanelCard title={t('map.sidebar.alertsTitle', 'Weather Alerts')} onClose={onClose}>
            <div className="px-[14px] pb-[14px]">
              <AlertsList weatherAlerts={weatherAlerts} extremeAlerts={extremeAlerts} isDark={isDark} t={t} navigate={navigate} />
            </div>
          </PanelCard>
        )}
        {mode === 'harvest' && (
          <PanelCard title={t('map.sidebar.harvestAlertsTitle', 'Harvest Alerts')} onClose={onClose}>
            <div className="px-[14px] pb-[14px]">
              <HarvestAlertsList harvestAlerts={harvestAlerts} isDark={isDark} t={t} onHoverRegion={onHoverRegion} onFocusRegion={onFocusRegion} />
            </div>
          </PanelCard>
        )}
      </div>
    </div>
  );
};

const HarvestAlertsList = ({ harvestAlerts, isDark, t, onHoverRegion, onFocusRegion }) => {
  if (!harvestAlerts || harvestAlerts.length === 0) {
    return <p className="text-sm text-[#5D625F] dark:text-[#828382]">{t('map.sidebar.noHarvestAlerts', 'No active harvest alerts')}</p>;
  }
  // "3 weeks ahead / behind" — computed from the signed week delta (not parsed from the message).
  return (
    <div className="space-y-3">
      {harvestAlerts.slice(0, 30).map((r, idx) => {
        const newStart = r.seasons?.[0]?.iso_week_start;
        const harvestWindow = r.seasons?.[0]?.harvest_window;
        const tons = formatTonsCompact(r.estimated_production_ton);
        const commodity = r.commodityKey || r.commodity;
        const status = r.harvest_status;
        return (
          <div
            key={`${regionMarkerId(r)}-${idx}`}
            onMouseEnter={() => onHoverRegion?.(regionMarkerId(r))}
            onMouseLeave={() => onHoverRegion?.(null)}
            onClick={() => onFocusRegion?.(r)}
            className="rounded-lg border-l-4 border-[#f59e0b] p-3 bg-[#F0F5F2] dark:bg-[#232121] flex flex-col gap-2 cursor-pointer transition-shadow hover:shadow-md"
          >
            {/* Row 1: new-start week + delta badge */}
            <div className="flex items-center justify-between">
              <span className="text-[0.6875rem] text-[#828382] font-medium">
                {newStart != null ? t('map.sidebar.newStartWeek', { week: newStart, defaultValue: `New start: Week ${newStart}` }) : ''}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-[#f59e0b]/20 text-[#b45309] dark:text-amber-300 font-medium text-[0.625rem]">
                {formatHarvestDelta(r.alert?.weeks_delta)}
              </span>
            </div>
            {/* Title = location */}
            <h4 className="font-semibold text-sm text-[#282A29] dark:text-[#ffffff] line-clamp-2">
              {toTitleCase(r.specific_location || r.region)}
            </h4>
            {/* Commodity */}
            <CommodityBadges commodities={commodity ? [commodity] : []} />
            {/* Location */}
            <div className="text-xs text-[#5D625F] dark:text-[#a0a0a0]">
              {r.region ? `${toTitleCase(r.region)}, ` : ''}{toTitleCase(r.country)}
            </div>
            {/* Reason */}
            {r.alert?.message && (
              <p className="text-xs text-[#5D625F] dark:text-[#828382] line-clamp-3">
                {r.alert.message}
              </p>
            )}
            {/* Harvest window (ISO weeks) + production — same info as the map tooltip */}
            {(harvestWindow || tons) && (
              <div className="text-xs text-[#5D625F] dark:text-[#828382]">
                {harvestWindow || ''}
                {harvestWindow && tons ? ' · ' : ''}
                {tons || ''}
              </div>
            )}
            {/* Footer */}
            <div className="pt-2 border-t border-[#C3C4C3]/30 dark:border-[#828382]/30 space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-bloom-green" />
                  <span className="text-bloom-green font-medium uppercase text-[0.625rem]">{t('home.alertsDigest.active', 'Active')}</span>
                </div>
                {status && STATUS_LABELS[status] && (
                  <div className="flex items-center gap-1">
                    <span className="text-[0.625rem] text-[#828382]">{t('map.sidebar.status', 'Status:')}</span>
                    <span
                      className="px-1.5 py-0.5 rounded font-medium uppercase text-[0.5625rem] text-white"
                      style={{ backgroundColor: STATUS_COLORS[status] || '#828382' }}
                    >
                      {STATUS_LABELS[status]}
                    </span>
                  </div>
                )}
              </div>
              {r.alert?.published_at && (
                <div className="text-[0.625rem] text-[#828382] italic">{formatLastUpdated(r.alert.published_at, t)}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Tags a variety row with the shipment it came from, so a variety that arrives
 * on several shipments can show attributed subcounts instead of repeated rows.
 */
const withSource = (row, shipment) => ({
  ...row,
  __source: shipment.vessel || shipment.container_number || null,
});

/**
 * One variety inside an ETA group. When the variety arrived on more than one
 * shipment the row rolls the weights up and the per-shipment subcounts are
 * revealed on click.
 */
const VarietyRow = ({ entry, weightUnit, t }) => {
  const [open, setOpen] = useState(false);
  const unit = weightUnitLabel(weightUnit);
  // Sum the converted subcounts rather than converting the kg total, so the
  // parent row always equals the rows shown under it (no rounding drift).
  const totalValue = entry.sources.reduce(
    (sum, src) => sum + convertWeight(Number(src.weight_kg) || 0, weightUnit), 0
  );
  const unavailable = entry.sources.some((src) => src.weight_unavailable);
  const total = unavailable ? t('map.sidebar.weightUnavailable', 'n/a') : `${totalValue.toLocaleString()} ${unit}`;

  if (entry.sources.length <= 1) {
    return (
      <div className="flex items-center justify-between gap-[8px] py-[3px]">
        <span className="flex min-w-0 items-center gap-[6px]">
          <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-[#C3C4C3] dark:bg-[#5d625f]" />
          <span className="font-['Inter'] truncate text-[11px] leading-[16px] text-[#5D625F] dark:text-[#828382]">
            {entry.variety}
          </span>
        </span>
        <span className="font-['Inter'] whitespace-nowrap text-[11px] leading-[16px] text-[#282A29] dark:text-[#f0f5f2]">
          {total}
        </span>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between gap-[8px] py-[3px] text-left transition-colors"
      >
        <span className="flex min-w-0 items-center gap-[4px]">
          <ChevronRight
            size={11}
            className={`shrink-0 text-[#828382] transition-transform ${open ? 'rotate-90' : ''}`}
          />
          <span className="font-['Inter'] truncate text-[11px] leading-[16px] text-[#5D625F] dark:text-[#828382]">
            {entry.variety}
          </span>
          <span className="shrink-0 text-[10px] text-[#828382]">({entry.sources.length})</span>
        </span>
        <span className="font-['Inter'] whitespace-nowrap text-[11px] font-medium leading-[16px] text-[#282A29] dark:text-[#f0f5f2]">
          {total}
        </span>
      </button>
      {open && (
        <div className="ml-[7px] border-l border-[#E8EBE9] pl-[8px] dark:border-[#30363d]">
          {entry.sources.map((src, idx) => (
            <div key={idx} className="flex justify-between gap-2 py-px text-[10px] text-[#828382]">
              <span className="truncate">{src.__source || t('map.sidebar.unknownVessel', 'Unknown vessel')}</span>
              <span className="whitespace-nowrap">
                {src.weight_unavailable
                  ? t('map.sidebar.weightUnavailable', 'n/a')
                  : `${convertWeight(Number(src.weight_kg) || 0, weightUnit).toLocaleString()} ${unit}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ArrivalsList = ({ shipments, t, timeWindow = 7, weightUnit = 'boxes', title, onClose, selectedCommodity = '' }) => {
  if (shipments.length === 0) {
    return (
      <PanelCard title={title} onClose={onClose}>
        <p className="px-[14px] pb-[14px] text-[12px] text-[#5D625F] dark:text-[#828382]">
          {t('map.sidebar.noArrivals', { days: timeWindow, defaultValue: `No shipments arriving in ${timeWindow} days` })}
        </p>
      </PanelCard>
    );
  }

  // Group by individual commodity → ETA → merge variety types/varieties
  // Shipments with multiple commodities (e.g. "Blueberries, Citrus") are split into separate groups
  const commodityFilter = String(selectedCommodity || '').trim().toLowerCase();
  const byCommodity = {};
  shipments.forEach((s) => {
    // Air/truck rows carry one total_weight and no per-variety breakdown. A mixed
    // group shows once under a combined label (never repeated per commodity). Under
    // a commodity filter a mixed group is still listed (the scorecards count it) but
    // its weight is unavailable, since it can't be split per commodity.
    if (s.transport_mode === 'air' || s.transport_mode === 'truck') {
      const cls = classifyAirTruckRow(s, commodityFilter);
      if (!cls) return;
      const { label, weightUnavailable } = cls;
      const modeEta = predictedEta(s) || '—';
      if (!byCommodity[label]) byCommodity[label] = { weight: 0, byEta: {}, shipmentKeys: new Set(), weightUnavailable };
      byCommodity[label].shipmentKeys.add(s.container_number || s.awb || s.truck_id || modeEta);
      if (!byCommodity[label].byEta[modeEta]) byCommodity[label].byEta[modeEta] = [];
      const kg = weightUnavailable ? 0 : Number(s.total_weight) || 0;
      byCommodity[label].byEta[modeEta].push({
        variety: s.varieties || '—',
        type_of_variety: s.transport_mode === 'air' ? 'Air' : 'Truck',
        weight_kg: kg,
        weight_unavailable: weightUnavailable,
        __source: s.awb || s.truck_id || s.container_number || null,
      });
      byCommodity[label].weight += kg;
      return;
    }
    let commodityList = (s.commodities || 'Unknown').split(',').map(c => c.trim()).filter(Boolean);
    if (commodityList.length === 0) commodityList.push('Unknown');

    if (commodityFilter) {
      commodityList = commodityList.filter((c) => c.toLowerCase() === commodityFilter);
      if (commodityList.length === 0) return;
    }

    const nestedWeight = getShipmentWeightNested(s);
    const eta = predictedEta(s) || '—';

    const hasCommodityGrouped = nestedWeight.length > 0 && nestedWeight[0]?.commodity;

    // When a filter is active only trust it for a single-commodity hull.
    const carriedCount = (s.commodities || '').split(',').map(c => c.trim()).filter(Boolean).length;
    if (commodityFilter && !hasCommodityGrouped && carriedCount > 1) return;

    commodityList.forEach((commodity) => {
      if (!byCommodity[commodity]) byCommodity[commodity] = { weight: 0, byEta: {}, shipmentKeys: new Set() };
      byCommodity[commodity].shipmentKeys.add(s.container_number || s.vessel || eta);
      if (!byCommodity[commodity].byEta[eta]) byCommodity[commodity].byEta[eta] = [];

      if (hasCommodityGrouped) {
        const match = nestedWeight.find(
          (v) => v.commodity && commodity && String(v.commodity).toLowerCase() === String(commodity).toLowerCase()
        );
        if (match) {
          (match.types || []).forEach((row) => byCommodity[commodity].byEta[eta].push(withSource(row, s)));
          byCommodity[commodity].weight += Number(match.total_weight) || 0;
        }
      } else {
        const rows = nestedWeight.flatMap((nw) => (nw.types?.length ? nw.types : [nw]));
        rows.forEach((v) => byCommodity[commodity].byEta[eta].push(withSource(v, s)));
        byCommodity[commodity].weight += rows.reduce(
          (sum, v) => sum + (Number(v.weight_kg) || Number(v.total_weight) || 0),
          0
        );
      }
    });
  });

  // Sort commodities A→Z
  const sortedCommodities = Object.entries(byCommodity).sort(([a], [b]) => a.localeCompare(b));

  const fmt = (kg) => `${convertWeight(kg, weightUnit).toLocaleString()} ${weightUnitLabel(weightUnit)}`;

  // One card per commodity — each repeats the window title and can be closed,
  // matching the stacked-card column in the spec.
  return (
    <>
      {sortedCommodities.map(([commodity, { weight, byEta, shipmentKeys, weightUnavailable }]) => {
        // Sort ETAs ascending
        const sortedEtas = Object.entries(byEta).sort(([a], [b]) => {
          const da = new Date(a), db = new Date(b);
          if (isNaN(da)) return 1;
          if (isNaN(db)) return -1;
          return da - db;
        });

        const shipLabel = t('map.scorecards.shipments', 'shipments');
        const fmtGroup = (kg) => (weightUnavailable ? t('map.sidebar.weightUnavailable', 'n/a') : fmt(kg));
        const subtitle = `${commodity} · ${shipmentKeys.size} ${shipLabel} · ${fmtGroup(weight)}`;

        return (
          <PanelCard key={commodity} title={title} subtitle={subtitle} onClose={onClose}>
            {sortedEtas.map(([eta, varieties]) => {
              const groups = groupByVarietyType(varieties);
              const etaWeight = varieties.reduce((sum, v) => sum + (Number(v.weight_kg) || 0), 0);

              return (
                <div key={eta}>
                  {/* ETA band */}
                  <div className={`flex items-center justify-between gap-[8px] border-y ${CARD_DIVIDER} bg-[#F7F8F7] px-[14px] py-[6px] dark:bg-[#232121]`}>
                    <span className="font-['Inter'] truncate text-[11px] font-semibold leading-[16px] text-[#282A29] dark:text-[#f0f5f2]">
                      ETA: {eta}
                    </span>
                    <span className="font-['Inter'] whitespace-nowrap text-[11px] font-semibold leading-[16px] text-bloom-green">
                      {fmtGroup(etaWeight)}
                    </span>
                  </div>

                  <div className="px-[14px] py-[8px]">
                    {groups.map((group) => (
                      <div key={group.type} className="mb-[8px] last:mb-0">
                        <div className={CAPTION_CLASS}>{group.type}</div>
                        {mergeVarietyRows(group.varieties).map((entry, vi) => (
                          <VarietyRow key={`${entry.variety}-${vi}`} entry={entry} weightUnit={weightUnit} t={t} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Card total */}
            <div className={`flex items-center gap-[6px] border-t ${CARD_DIVIDER} px-[14px] py-[10px]`}>
              <span className={CAPTION_CLASS}>{t('map.sidebar.total', 'Total')}</span>
              <span className="font-['Inter'] text-[12px] font-semibold leading-[18px] text-[#282A29] dark:text-[#f0f5f2]">
                {fmtGroup(weight)}
              </span>
            </div>
          </PanelCard>
        );
      })}
    </>
  );
};

const toTitleCase = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

const formatAlertType = (type) => {
  if (!type) return '';
  return type
    .split('_')
    .map((w) => (w.toLowerCase() === 'fob' ? 'FOB' : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
};

const getImpactColors = (impact) => {
  const level = (impact || '').toLowerCase();
  if (level === 'high' || level === 'alto') return { border: 'border-red-500', bg: 'bg-red-500', text: 'text-white' };
  if (level === 'medium' || level === 'medio') return { border: 'border-orange-500', bg: 'bg-orange-500', text: 'text-white' };
  if (level === 'low' || level === 'bajo') return { border: 'border-bloom-green', bg: 'bg-bloom-green', text: 'text-white' };
  return { border: 'border-[#828382]', bg: 'bg-[#828382]', text: 'text-white' };
};

const getExtremeAlertColors = (alertType) => {
  const type = (alertType || '').toUpperCase();
  switch (type) {
    case 'FREEZE': return { border: 'border-blue-500', bgLight: 'bg-blue-500/20', textColor: 'text-blue-500' };
    case 'EXTREME_HEAT': return { border: 'border-orange-600', bgLight: 'bg-orange-600/20', textColor: 'text-orange-600' };
    case 'DROUGHT': return { border: 'border-amber-600', bgLight: 'bg-amber-600/20', textColor: 'text-amber-600' };
    case 'FLOODING': return { border: 'border-cyan-600', bgLight: 'bg-cyan-600/20', textColor: 'text-cyan-600' };
    case 'STORM': case 'HURRICANE': return { border: 'border-purple-600', bgLight: 'bg-purple-600/20', textColor: 'text-purple-600' };
    default: return { border: 'border-gray-500', bgLight: 'bg-gray-500/20', textColor: 'text-gray-500' };
  }
};

const formatAlertDate = (alert, t) => {
  if (alert.iso_week && alert.iso_year) {
    return t('home.weatherDigest.weekFormat', {
      week: alert.iso_week, year: alert.iso_year,
      defaultValue: `Week ${alert.iso_week}, ${alert.iso_year}`,
    });
  }
  return '';
};

const formatLastUpdated = (dateStr, t) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const formatted = date.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
    });
    return t('home.alertsDigest.lastUpdated', { date: formatted, defaultValue: `Last updated: ${formatted}` });
  } catch { return null; }
};

const CommodityBadges = ({ commodities, max = 3 }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);

  const list = (typeof commodities === 'string'
    ? commodities.split(',')
    : Array.isArray(commodities) ? commodities : []
  ).map(c => c.trim()).filter(Boolean);

  const visible = list.slice(0, max);
  const hidden = list.slice(max);
  const hasMore = hidden.length > 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };
    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTooltip]);

  if (list.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((c, i) => (
        <span key={i} className="px-2 py-0.5 rounded-full bg-bloom-green/20 text-bloom-green font-medium text-[0.625rem]">
          {toTitleCase(c)}
        </span>
      ))}
      {hasMore && (
        <div className="relative" ref={tooltipRef}>
          <span
            className="px-2 py-0.5 rounded-full bg-[#828382]/20 text-[#828382] font-medium text-[0.625rem] cursor-pointer hover:bg-[#828382]/30 transition-colors select-none"
            onClick={() => setShowTooltip(!showTooltip)}
            onTouchEnd={(e) => { e.preventDefault(); setShowTooltip(!showTooltip); }}
          >
            +{hidden.length}
          </span>
          {showTooltip && (
            <div className="absolute bottom-full left-0 mb-1 p-2 rounded-lg bg-[#F0F5F2] dark:bg-[#3a3c3b] shadow-lg border border-[#C3C4C3] dark:border-[#828382] z-30 min-w-max max-w-[200px] max-h-[300px] overflow-y-auto">
              <div className="flex flex-col items-start gap-1">
                {hidden.map((c, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-full bg-bloom-green/20 text-bloom-green font-medium text-[0.625rem] whitespace-nowrap">
                    {toTitleCase(c)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Determines if an alert is active using a unified check that works for both
 * weather digest alerts (is_active field) and extreme alerts (status field).
 */
const isAlertActive = (alert) => {
  // Extreme alerts use status === 'Active'
  if (alert.status !== undefined) {
    return String(alert.status).toLowerCase() === 'active';
  }
  // Weather digest alerts use is_active (boolean/string/number)
  if (alert.is_active !== undefined) {
    const v = alert.is_active;
    if (v === false || v === 'false' || v === 'False' || v === 0 || v === '0') return false;
    return true;
  }
  // Neither field present — treat as inactive
  return false;
};

const AlertsList = ({ weatherAlerts, extremeAlerts, isDark, t, navigate }) => {
  // Filter extreme alerts: must be active AND high/alto impact
  const filteredExtreme = extremeAlerts.filter(a =>
    isAlertActive(a) && ['high', 'alto'].includes((a.market_impact || '').toLowerCase())
  );

  // Filter weather alerts: must be active AND high/alto impact
  // Also exclude any that would duplicate extreme alerts (same specific_alert_type + region + country)
  const extremeKeys = new Set(filteredExtreme.map(a =>
    `${(a.specific_alert_type || '').toLowerCase()}-${(a.region || '').toLowerCase()}-${(a.country || '').toLowerCase()}`
  ));

  const filteredWeather = weatherAlerts.filter(a => {
    if (!isAlertActive(a)) return false;
    if (!['high', 'alto'].includes((a.market_impact || '').toLowerCase())) return false;
    // If this weather alert has specific_alert_type, check it's not already in extreme list
    if (a.specific_alert_type) {
      const key = `${a.specific_alert_type.toLowerCase()}-${(a.region || '').toLowerCase()}-${(a.country || '').toLowerCase()}`;
      if (extremeKeys.has(key)) return false;
    }
    return true;
  });

  const allAlerts = [...filteredExtreme, ...filteredWeather];

  if (allAlerts.length === 0) {
    return <p className="text-sm text-[#5D625F] dark:text-[#828382]">{t('map.sidebar.noAlerts', 'No active weather alerts')}</p>;
  }

  return (
    <div className="space-y-3">
      {allAlerts.slice(0, 30).map((alert, idx) => {
        const isActive = isAlertActive(alert);
        const hasAlertType = !!alert.specific_alert_type;
        const alertTypeColors = hasAlertType ? getExtremeAlertColors(alert.specific_alert_type) : null;

        // Border color: use impact colors if market_impact exists, else alert type colors, else gray
        const impactColors = getImpactColors(alert.market_impact);
        const borderColor = !isActive
          ? 'border-[#9CA3AF]'
          : alert.market_impact
            ? impactColors.border
            : alertTypeColors
              ? alertTypeColors.border
              : 'border-[#828382]';
        const bgColor = !isActive ? 'bg-[#F3F4F6] dark:bg-[#1F1F1F]' : 'bg-[#F0F5F2] dark:bg-[#232121]';

        // Title: use alert.title if available, otherwise compose from region/country
        const title = alert.title
          || `${alert.region ? `${toTitleCase(alert.region)}, ` : ''}${toTitleCase(alert.country)}`;

        // Commodities: use commodities_affected, fall back to single commodity
        const commodities = alert.commodities_affected || (alert.commodity ? [alert.commodity] : []);

        return (
          <div key={idx} className={`rounded-lg border-l-4 ${borderColor} p-3 ${bgColor} flex flex-col gap-2`}>
            {/* Row 1: Date + type badge (if extreme) */}
            <div className="flex items-center justify-between">
              <span className="text-[0.6875rem] text-[#828382] font-medium">{formatAlertDate(alert, t)}</span>
              {hasAlertType && alertTypeColors && (
                <span className={`px-2 py-0.5 rounded-full ${alertTypeColors.bgLight} ${alertTypeColors.textColor} font-medium text-[0.625rem]`}>
                  {formatAlertType(alert.specific_alert_type)}
                </span>
              )}
            </div>
            {/* Title */}
            <h4 className="font-semibold text-sm text-[#282A29] dark:text-[#ffffff] line-clamp-2">
              {title}
            </h4>
            {/* Commodities */}
            <CommodityBadges commodities={commodities} />
            {/* Location (only if title doesn't already contain it) */}
            {alert.title && alert.country && (
              <div className="text-xs text-[#5D625F] dark:text-[#a0a0a0]">
                {alert.region ? `${toTitleCase(alert.region)}, ` : ''}{toTitleCase(alert.country)}
              </div>
            )}
            {/* Summary */}
            <p className="text-xs text-[#5D625F] dark:text-[#828382] line-clamp-3">
              {alert.impact_summary || alert.summary || alert.description || ''}
            </p>
            {/* Footer */}
            <div className="pt-2 border-t border-[#C3C4C3]/30 dark:border-[#828382]/30 space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  {isActive ? (
                    <>
                      <span className="w-2 h-2 rounded-full bg-bloom-green" />
                      <span className="text-bloom-green font-medium uppercase text-[0.625rem]">{t('home.alertsDigest.active', 'Active')}</span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 rounded-full border border-[#828382]" />
                      <span className="text-[#828382] font-medium uppercase text-[0.625rem]">{t('home.alertsDigest.inactive', 'Inactive')}</span>
                    </>
                  )}
                </div>
                {alert.market_impact && (
                  <div className="flex items-center gap-1">
                    <span className="text-[0.625rem] text-[#828382]">{t('home.weatherDigest.impact', 'Impact:')}</span>
                    <span className={`px-1.5 py-0.5 rounded font-medium uppercase text-[0.5625rem] ${isActive ? `${impactColors.bg} ${impactColors.text}` : 'bg-[#9CA3AF] text-white'}`}>
                      {alert.market_impact}
                    </span>
                  </div>
                )}
              </div>
              {alert.last_evaluated_at && (
                <div className="text-[0.625rem] text-[#828382] italic">{formatLastUpdated(alert.last_evaluated_at, t)}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MapSidebar;
